import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getCustomizedNavTitle } from 'lib/helpers';
import { Search } from 'components/common';
import {
  Page,
  PageContent,
  PageEmptyIndicator,
  PageHeader,
  PageInnerContent
} from 'components/page';
import { AchievementsCreate, AchievementsNav } from '.';

import type { GetCategoriesRes } from 'shared-types/responses';

interface Props {
  children: React.ReactNode;
  isRootPage?: boolean;
  showNotFound?: boolean;
}

const AchievementsPage = ({
  children,
  isRootPage = false,
  showNotFound
}: Props) => {
  const { language, messages, serverConfig, user } = useReduxState();

  const router = useRouter();

  const [previousView, setPreviousView] = useState<string>();

  const { data: categoriesData } = useApiData<GetCategoriesRes>({
    errorMessage: messages.categoriesGetCategoriesError,
    url: '/admin/categories-achievements'
  });

  const type = router?.query.type as
    | 'active'
    | 'administered'
    | 'all'
    | 'available'
    | 'individual' // deprecated
    | 'search';

  useEffect(() => {
    if (isRootPage && !type) pushToRoute();

    // individual achievements have been moved to a distinct path, this
    // redirect handles old links ex. from notifications
    if (type === 'individual') router.replace('/achievements/individual');
  }, [type]);

  const pushToRoute = (route?: string) => {
    const type = route
      ? route
      : user?.roles.includes('superadmin')
      ? 'all'
      : user?.roles.includes('admin')
      ? 'administered'
      : 'active';
    router.replace(`/achievements?type=${type}`, `/achievements?type=${type}`, {
      shallow: true
    });
  };

  const handleForceUpdate = () => {
    // we forcibly refresh the page to update the data
    // after importing a JSON file
    router.replace(
      { query: { ...router.query, _refresh: Date.now() } },
      undefined,
      { shallow: true }
    );
  };

  const handleSearch = ({ search }: { search: string }) => {
    if (search) {
      if (type !== 'search') setPreviousView(type);
      router.push(
        `/achievements?type=search&search=${search}`,
        `/achievements?type=search&search=${search}`,
        { shallow: true }
      );
    } else pushToRoute(previousView);
  };

  if (isRootPage && !type) return null;

  return (
    <Page activePathname="/achievements">
      <PageHeader
        icon="check-circle"
        title={getCustomizedNavTitle({
          defaultTitle: messages.commonAchievementsAlt,
          navLinksCustomTexts: serverConfig.navLinksCustomTexts,
          language,
          section: 'achievements'
        })}
      >
        {isRootPage ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ minWidth: 300 }}>
              <Search
                onSearch={handleSearch}
                placeholder={messages.achievementSearch}
                size="large"
              />
            </div>

            {(user?.roles.includes('admin') &&
              user?.permissions.includes('create-achievements')) ||
            user?.roles.includes('superadmin') ? (
              <AchievementsCreate updateData={handleForceUpdate} />
            ) : null}
          </div>
        ) : null}
      </PageHeader>

      {showNotFound ? (
        <PageEmptyIndicator
          icon="check-circle"
          title={messages.errorNotFound}
          description={messages.errorNotFoundInfo}
        />
      ) : (
        <PageContent withSidebar={true}>
          <AchievementsNav categories={categoriesData?.categories} />

          <PageInnerContent>{children}</PageInnerContent>
        </PageContent>
      )}
    </Page>
  );
};

export default AchievementsPage;
